import * as THREE from "three";
import { defineModel, type Part, type Vec3 } from "../../lib";

// Tesla Cybertruck (2023) — the origami stainless-steel wedge at true scale
// (1 unit = 1 metre): length ~5.68 (huge), width 2.03 (very WIDE), height 1.79,
// wheelbase 3.62, ~0.42 m wheels (huge). Nose is +Z. Authored for the `cars`
// showcase batch.
//
// The whole silhouette is ONE giant flat triangular WEDGE — a doorstop: from the
// side it is a clean symmetric triangle. A single straight line climbs from the
// low front point up the hood and blacked-out windscreen to a central roof peak,
// then a single straight line falls all the way down the integrated bed "vault"
// cover to the tail. There is NO separate cab + open bed; the top is one
// continuous flat slope of huge faceted brushed-steel planes with razor-sharp
// edges and no curves anywhere. The defining cues: BARE BRUSHED STAINLESS STEEL
// bodywork (flat medium-light grey, on the "paint" slot so it can still be
// recoloured), the signature FULL-WIDTH LIGHT BARS — one thin white LED line
// edge-to-edge across the entire nose and an identical red line across the entire
// tail — a very dark blacked-out greenhouse of flat raked glass with thin black
// pillars, a black lower cladding band with angular TRAPEZOIDAL wheel arches (not
// round), a black lower bumper front and rear, huge chunky tyres on flat angular
// seven-hole aero wheel covers. `drive` spins the wheels and `hazards` blinks the
// four indicators.

const PAINT = "#b9bdc1"; // brushed stainless steel — flat medium-light grey, the "paint" slot default
const TRIM = "#141518"; // gloss black (pillars, beltline, mirror stalks)
const CLAD = "#1b1d20"; // black lower cladding band, angular arches, bumpers
const WELL = "#0d0e10"; // wheel-well backing
const GLASS = "#0c0f12"; // very dark blacked-out greenhouse glazing
const WHEELF = "#c7ccd1"; // flat silver angular wheel-cover face
const WHEELDK = "#17181b"; // dark aero-cover slots + hub
const TIRE = "#131416"; // huge tyre
const BRAKE = "#8c9196";
const LED = "#f2f7fc"; // full-width front white light bar
const REDBAR = "#d21a2a"; // full-width rear red light bar
const CHROME = "#ccd1d6";
const AMBER = "#e8a13a";
const PLATE = "#eceff2";

// Spec-sheet skeleton (metres). Huge, wide, tall-but-wider-than-tall truck.
const WHEEL_R = 0.42; // huge tyres
const AXLE_F = 1.81;
const AXLE_R = -1.81; // wheelbase 3.62
const WHEEL_X = 0.93; // half track (wide, planted stance; tyres show at the corners)
const TYRE_W = 0.32;
const TYRE_OUT = WHEEL_X + TYRE_W / 2; // tyre outer face — stays proud of the body/arch
const BODY_X = 0.98; // steel tub half-width (body 1.96; tyres plant proud of it)

// The single-wedge ridge (side profile, front +z to rear -z), as breakpoints
// (z, top-y). Hood and windscreen share ONE straight rake up to a short flat
// roof peak; the rear glass and the long steel "vault" bed cover share ONE
// straight rake down to the tail. So the side view is a clean triangle.
//   N nose top (2.84, 0.82) -> K cowl (1.20, 1.472) -> P peak front (0.40, 1.79)
//   -> Q peak rear (-0.10, 1.79) -> R rear-glass base (-1.15, 1.510)
//   -> T tail top (-2.84, 1.06)
// N->K->P is collinear (one front line); Q->R->T is collinear (one rear line).

function box(
  w: number, h: number, d: number, color: string,
  position: Vec3, rotation?: Vec3, extra?: Partial<Part>,
): Part {
  return { geometry: new THREE.BoxGeometry(w, h, d), color, position, rotation, ...extra };
}

function cyl(
  rTop: number, rBot: number, h: number, seg: number, color: string,
  position: Vec3, rotation?: Vec3, extra?: Partial<Part>,
): Part {
  return { geometry: new THREE.CylinderGeometry(rTop, rBot, h, seg), color, position, rotation, ...extra };
}

/** A full-width box whose TOP face runs along the side-profile line from
 *  (zF,yF) at the front to (zB,yB) at the back, extending downward by `t`. The
 *  box is tilted about x so its top edge lies exactly on the ridge line — this
 *  is how the single triangular wedge is assembled from a few flat planes. */
function slope(
  zF: number, yF: number, zB: number, yB: number,
  w: number, t: number, color: string, paintSlot: boolean,
): Part {
  const theta = Math.atan2(yB - yF, zF - zB); // +theta => front (+z) edge lower
  const depth = Math.hypot(zF - zB, yF - yB);
  const zc = (zF + zB) / 2;
  const yc = (yF + yB) / 2;
  const cy = yc - (t / 2) * Math.cos(theta); // drop the box so its top lies on the line
  const cz = zc - (t / 2) * Math.sin(theta);
  return box(w, t, depth, color, [0, cy, cz], [theta, 0, 0], paintSlot ? { slot: "paint" } : undefined);
}

/** A painted body panel — carries the "paint" recolour slot. */
function paint(w: number, h: number, d: number, position: Vec3, rotation?: Vec3): Part {
  return box(w, h, d, PAINT, position, rotation, { slot: "paint" });
}

/** One huge Cybertruck wheel: a big tyre, a brake disc, a FLAT ANGULAR silver
 *  seven-sided aero-cover face carrying seven dark radial slots, a dark hub and
 *  a small chrome cap. Every part shares `name` so the `drive` track spins them
 *  as one node about the axle. `side` is +1 for the right (+x) wheel, -1 left. */
function wheel(side: 1 | -1, z: number, name: string): Part[] {
  const x = side * WHEEL_X;
  const y = WHEEL_R;
  const outer = TYRE_OUT;
  const parts: Part[] = [
    cyl(WHEEL_R, WHEEL_R, TYRE_W, 22, TIRE, [x, y, z], [0, 0, Math.PI / 2], { name }),
    cyl(0.17, 0.17, 0.03, 14, BRAKE, [side * (WHEEL_X - 0.06), y, z], [0, 0, Math.PI / 2], { name }),
    cyl(0.33, 0.33, 0.06, 7, WHEELF, [side * (outer - 0.03), y, z], [0, 0, Math.PI / 2], { name }), // angular heptagon face
  ];
  for (let k = 0; k < 7; k++) {
    const a = (k * 2 * Math.PI) / 7;
    parts.push(
      box(0.06, 0.24, 0.05, WHEELDK, // dark angular radial aero slot, proud of the face
        [side * (outer - 0.005), y + 0.185 * Math.cos(a), z + 0.185 * Math.sin(a)],
        [a, 0, 0], { name }),
    );
  }
  parts.push(cyl(0.1, 0.1, 0.05, 7, WHEELDK, [side * (outer - 0.005), y, z], [0, 0, Math.PI / 2], { name })); // dark hub
  parts.push(cyl(0.05, 0.05, 0.03, 12, CHROME, [side * (outer + 0.005), y, z], [0, 0, Math.PI / 2], { name })); // centre cap
  return parts;
}

/** An ANGULAR trapezoidal wheel arch (never round): a black horizontal top bar
 *  over the wheel plus two black slanted side bars, all just proud of the body,
 *  with a dark well backing behind. The tyre's outer face stays proud of it. */
function arch(side: 1 | -1, z: number): Part[] {
  const xb = side * (BODY_X + 0.005);
  const xf = side * (BODY_X + 0.03);
  return [
    box(0.05, 0.92, 1.02, WELL, [xb, WHEEL_R + 0.05, z]), // dark well backing
    box(0.08, 0.12, 1.06, CLAD, [xf, WHEEL_R + 0.46, z]), // trapezoid top bar
    box(0.08, 0.58, 0.12, CLAD, [xf, WHEEL_R + 0.14, z + 0.56], [-0.5, 0, 0]), // slanted leading edge
    box(0.08, 0.58, 0.12, CLAD, [xf, WHEEL_R + 0.14, z - 0.56], [0.5, 0, 0]), // slanted trailing edge
  ];
}

// The four hazard indicators — parts and blink tracks are generated from one
// table so the pivots (each part's own centre) can never drift from the parts.
// They sit at the outer ends of the two full-width light bars.
const INDICATORS: ReadonlyArray<{ name: string; pos: Vec3; size: Vec3 }> = [
  { name: "indicator-fl", pos: [-0.85, 0.88, 2.9], size: [0.13, 0.055, 0.05] },
  { name: "indicator-fr", pos: [0.85, 0.88, 2.9], size: [0.13, 0.055, 0.05] },
  { name: "indicator-rl", pos: [-0.85, 0.97, -2.9], size: [0.13, 0.055, 0.05] },
  { name: "indicator-rr", pos: [0.85, 0.97, -2.9], size: [0.13, 0.055, 0.05] },
];

function parts(): Part[] {
  const P: Part[] = [];
  const S = [1, -1] as const;

  // -- Running gear (huge tyres planted at the wide corners) -------------------
  P.push(
    ...wheel(-1, AXLE_F, "wheel-fl"), ...wheel(1, AXLE_F, "wheel-fr"),
    ...wheel(-1, AXLE_R, "wheel-rl"), ...wheel(1, AXLE_R, "wheel-rr"),
  );
  for (const z of [AXLE_F, AXLE_R]) {
    P.push(cyl(0.06, 0.06, 1.7, 8, TRIM, [0, WHEEL_R, z], [0, 0, Math.PI / 2])); // axle
    for (const s of S) P.push(...arch(s, z));
  }
  P.push(box(1.7, 0.18, 5.2, TRIM, [0, 0.28, -0.1])); // underbody pan

  // -- Lower body: black cladding band + flat steel sides ----------------------
  P.push(box(2.02, 0.32, 5.4, CLAD, [0, 0.30, -0.15])); // black lower cladding band (y 0.14..0.46)
  P.push(paint(1.96, 0.60, 4.85, [0, 0.75, -0.175])); // flat steel sides / doors (y 0.45..1.05)

  // -- The single triangular wedge (one continuous flat slope) -----------------
  // Front line: steel hood -> blacked-out windscreen, one straight rake.
  P.push(slope(2.84, 0.82, 1.20, 1.472, 1.92, 0.70, PAINT, true)); // steel hood (nose -> cowl)
  P.push(slope(1.20, 1.472, 0.40, 1.79, 1.70, 0.55, GLASS, false)); // raked windscreen (cowl -> peak)
  // Short flat roof peak.
  P.push(slope(0.40, 1.79, -0.10, 1.79, 1.76, 0.14, PAINT, true)); // steel roof cap
  P.push(box(1.70, 0.80, 0.55, GLASS, [0, 1.40, 0.15])); // dark cabin fill under the roof (seals the flanks)
  // Rear line: blacked-out rear glass -> long steel bed "vault" cover, one rake.
  P.push(slope(-0.10, 1.79, -1.15, 1.510, 1.70, 0.55, GLASS, false)); // raked rear glass (peak -> vault)
  P.push(slope(-1.15, 1.510, -2.84, 1.06, 1.92, 0.70, PAINT, true)); // steel vault / bed cover (vault -> tail)

  // -- Razor-thin black pillars + beltline on the greenhouse -------------------
  const wsTheta = Math.atan2(1.79 - 1.472, 1.20 - 0.40); // windscreen rake
  const rgTheta = Math.atan2(1.510 - 1.79, -0.10 + 1.15); // rear-glass rake
  for (const s of S) {
    P.push(box(0.05, 0.09, 0.9, TRIM, [s * 0.84, 1.63, 0.80], [wsTheta, 0, 0])); // A-pillar
    P.push(box(0.05, 0.09, 1.1, TRIM, [s * 0.84, 1.65, -0.62], [rgTheta, 0, 0])); // C-pillar
    P.push(box(0.05, 0.10, 0.05, TRIM, [s * 0.85, 1.70, 0.15])); // B-pillar cap at the peak
    P.push(box(0.03, 0.03, 2.4, TRIM, [s * 0.985, 1.04, -0.05])); // beltline strip
  }

  // -- Front fascia: full-width WHITE light bar over the wedge nose ------------
  P.push(paint(1.92, 0.48, 0.10, [0, 0.72, 2.84])); // steel nose face (y 0.48..0.96)
  P.push(box(1.78, 0.30, 0.7, CLAD, [0, 0.40, 2.42])); // fill under the nose back to the front arch
  P.push(box(2.00, 0.30, 0.16, CLAD, [0, 0.40, 2.84])); // black front bumper (y 0.25..0.55)
  P.push(box(0.9, 0.10, 0.05, TRIM, [0, 0.40, 2.93])); // lower intake slot
  P.push(box(1.96, 0.14, 0.03, TRIM, [0, 0.92, 2.888])); // dark recess channel behind the light bar
  P.push(box(1.96, 0.08, 0.05, LED, [0, 0.92, 2.905])); // full-width front light bar (edge to edge)
  P.push(box(0.44, 0.13, 0.02, PLATE, [0, 0.65, 2.92])); // front plate

  // -- Rear fascia: full-width RED light bar over the vault tail ---------------
  P.push(paint(1.92, 0.52, 0.10, [0, 0.79, -2.84])); // steel tailgate face (y 0.53..1.05)
  P.push(box(1.78, 0.30, 0.7, CLAD, [0, 0.42, -2.42])); // fill under the tail back to the rear arch
  P.push(box(2.00, 0.32, 0.16, CLAD, [0, 0.42, -2.84])); // black rear bumper (y 0.26..0.58)
  P.push(box(1.96, 0.14, 0.03, TRIM, [0, 1.00, -2.888])); // dark recess channel behind the light bar
  P.push(box(1.96, 0.08, 0.05, REDBAR, [0, 1.00, -2.905])); // full-width rear light bar (edge to edge)
  P.push(box(0.44, 0.13, 0.02, PLATE, [0, 0.74, -2.92])); // rear plate

  // -- Furniture: minimal flush-style mirrors ---------------------------------
  for (const s of S) {
    P.push(box(0.1, 0.03, 0.05, TRIM, [s * 1.0, 1.04, 0.75])); // mirror stalk overlapping the body
    P.push(box(0.05, 0.12, 0.16, TRIM, [s * 1.07, 1.06, 0.75])); // mirror head
    P.push(box(0.012, 0.09, 0.12, GLASS, [s * 1.105, 1.06, 0.76])); // mirror glass, facing back
  }
  for (const ind of INDICATORS) {
    P.push(box(ind.size[0], ind.size[1], ind.size[2], AMBER, ind.pos, undefined, { name: ind.name }));
  }

  return P;
}

// One wheel revolution per cycle; positive x-rotation rolls the car toward +Z.
const WHEEL_PIVOTS: ReadonlyArray<{ name: string; pivot: Vec3 }> = [
  { name: "wheel-fl", pivot: [-WHEEL_X, WHEEL_R, AXLE_F] },
  { name: "wheel-fr", pivot: [WHEEL_X, WHEEL_R, AXLE_F] },
  { name: "wheel-rl", pivot: [-WHEEL_X, WHEEL_R, AXLE_R] },
  { name: "wheel-rr", pivot: [WHEEL_X, WHEEL_R, AXLE_R] },
];

export const teslaCybertruck = defineModel({
  id: "tesla-cybertruck",
  name: "Tesla Cybertruck",
  batchId: "cars",
  tags: ["car", "tesla", "cybertruck", "electric", "truck", "pickup", "modern"],
  summary: "A Tesla Cybertruck — one giant flat brushed-steel triangular wedge — at true scale.",
  description:
    "A 2023 Tesla Cybertruck at real 1 unit = 1 metre scale (about 5.68 long, a " +
    "very wide 2.03, 1.79 tall, 3.62 wheelbase, huge ~0.42 m wheels). The whole " +
    "silhouette is ONE giant flat triangular wedge — a doorstop: from the side " +
    "it reads as a clean symmetric triangle, a single straight line climbing " +
    "from the low front point up the hood and blacked-out windscreen to a " +
    "central roof peak, then a single straight line falling down the integrated " +
    "bed 'vault' cover to the tail, with no separate cab and open bed. Every " +
    "panel is a flat faceted brushed-steel plane with razor-sharp edges and no " +
    "curves. The signature cues are all present: bare brushed stainless-steel " +
    "bodywork (flat medium-light grey), the full-width light bars (one thin " +
    "white LED line edge-to-edge across the nose and an identical red line " +
    "across the tail), a very dark blacked-out greenhouse of raked glass with " +
    "thin black pillars, a black lower cladding band with angular trapezoidal " +
    "wheel arches, black lower bumpers, and huge chunky tyres on flat angular " +
    "seven-hole aero wheel covers. Body panels carry the 'paint' recolour slot " +
    "(brushed steel grey); `drive` spins the wheels and `hazards` blinks the " +
    "four indicators.",
  author: "Fable 5",
  footprint: 2.99,
  parts,
  animations: [
    {
      id: "drive",
      label: "Drive",
      duration: 1,
      tracks: WHEEL_PIVOTS.map(({ name, pivot }) => ({
        target: name,
        pivot,
        rotation: [
          { t: 0, value: [0, 0, 0] as Vec3 },
          { t: 0.5, value: [Math.PI, 0, 0] as Vec3 },
          { t: 1, value: [2 * Math.PI, 0, 0] as Vec3 },
        ],
      })),
    },
    {
      id: "hazards",
      label: "Hazard lights",
      duration: 1,
      tracks: INDICATORS.map(({ name, pos }) => ({
        target: name,
        pivot: pos,
        scale: [
          { t: 0, value: 1 },
          { t: 0.42, value: 1 },
          { t: 0.5, value: 0.02 },
          { t: 0.92, value: 0.02 },
          { t: 1, value: 1 },
        ],
      })),
    },
  ],
});
